import { TZDateMini } from '@date-fns/tz';
import { isAfter, isBefore } from 'date-fns';
import { type Dispatch, type RefObject } from 'react';
import {
  FormattedMessage,
  type IntlShape,
  type MessageDescriptor,
} from 'react-intl';

import { parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import {
  isFullTimeframe,
  isNowTimeValue,
  isPartialTimeframe,
  isTimeframe,
  isTimeValue,
  getFormattedMinMax,
  getFormattedTimeValue,
} from './helpers.js';
import type { Precision, TimeErrorKey } from './shared.types.js';
import { TIME_ERROR_MESSAGES as DEFAULT_ERROR_MESSAGES } from './timeErrorMessages.js';
import {
  type FormFieldMessagesReducerAction,
  FormFieldMessagesAction,
} from '../../forms/forms-core/state/formfield-messages-state-reducer.js';
import {
  GRAIL_MAX_DATE,
  GRAIL_MIN_DATE,
} from '../../forms/forms-core/time/date-time.js';
import { ValidityStateKey } from '../../forms/validation/types.js';
import { type Timeframe, type TimeValue } from '../types/time.js';

type TimeBoundaryErrorKey = Extract<
  TimeErrorKey,
  | 'maxBoundary'
  | 'maxBoundaryNowError'
  | 'minBoundary'
  | 'minBoundaryNow'
  | 'minMaxBoundary'
>;

type TimeBoundaryMessageValues = {
  min: string;
  max: string;
  term: string;
};

type TimeBoundaryMessageData = {
  errorKey: TimeBoundaryErrorKey;
  values: TimeBoundaryMessageValues;
};

/** @internal */
export type ValidateOptions = {
  formMessageDispatch?: Dispatch<FormFieldMessagesReducerAction>;
  formerError?: TimeErrorKey;
  precision?: Precision;
  showError?: boolean;
  checkOrder?: boolean;
  ref?: RefObject<unknown>;
  expectedFormat?: string;
  valueMode?: 'single' | 'timeframeSelector' | 'dateTimePicker';
  required?: boolean;
  /** Optional per-key message overrides. Falls back to built-in defaults for any omitted keys. @internal */
  errorMessages?: Partial<Record<TimeErrorKey, MessageDescriptor>>;
};

/**
 * Checks if a date is outside the given min and/or max bounds.
 * @internal
 */
export function isOutOfBounds(
  date?: string | Date,
  min?: TimeValue | null,
  max?: TimeValue | null,
): TimeErrorKey | undefined {
  // All parse times here need to use the same _now_ date, to not be subject
  // to millisecond differences during parsing.
  const relativeNow = TZDateMini.tz(getTimezone()).getTime();

  const newDate =
    typeof date === 'string'
      ? parseTimeAsTimeValue(date, relativeNow)?.absoluteDate
      : date;

  if (!newDate) {
    return undefined;
  }

  // Re-evaluate relative expressions using the same "now" as the value so
  // that min/max stay temporally consistent with the date being checked.
  // Using a stale absoluteDate from a midnight refresh would cause spurious
  // out-of-bounds errors when the value is evaluated at the current time.
  const minDate =
    min?.type === 'expression'
      ? parseTimeAsTimeValue(min.value, relativeNow)?.absoluteDate
      : min?.absoluteDate;
  const maxDate =
    max?.type === 'expression'
      ? parseTimeAsTimeValue(max.value, relativeNow)?.absoluteDate
      : max?.absoluteDate;

  if (
    minDate &&
    maxDate &&
    (isBefore(newDate, minDate) || isAfter(newDate, maxDate))
  ) {
    return 'minMaxBoundary';
  }

  if (minDate && !maxDate && isBefore(newDate, minDate)) {
    return 'minBoundary';
  }

  if (!minDate && maxDate && isAfter(newDate, maxDate)) {
    return 'maxBoundary';
  }

  return undefined;
}

/**
 * Checks whether any part of the given value falls outside the default Grail date range.
 */
function isValueOutsideGrailRange(
  value: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null,
): boolean {
  if (!value) {
    return false;
  }

  const grailMinDate = new Date(GRAIL_MIN_DATE);
  const grailMaxDate = new Date(GRAIL_MAX_DATE);

  if (isTimeValue(value)) {
    if (!value.absoluteDate) {
      return false;
    }
    const date = new Date(value.absoluteDate);
    return isBefore(date, grailMinDate) || isAfter(date, grailMaxDate);
  }

  const fromDate = value.from?.absoluteDate
    ? new Date(value.from.absoluteDate)
    : null;
  const toDate = value.to?.absoluteDate
    ? new Date(value.to.absoluteDate)
    : null;

  return (
    (fromDate !== null &&
      (isBefore(fromDate, grailMinDate) || isAfter(fromDate, grailMaxDate))) ||
    (toDate !== null &&
      (isBefore(toDate, grailMinDate) || isAfter(toDate, grailMaxDate)))
  );
}

/**
 * Filters a GRAIL default min or max TimeValue.
 * Returns `undefined` when the limit is a default Grail boundary and the value
 * is within the Grail range (so the Grail limit should not appear in messages).
 * Custom (non-Grail) limits are always kept.
 */
function filterGrailTimeValue(
  value: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null,
  limit?: TimeValue | null,
): TimeValue | null | undefined {
  if (!limit) {
    return limit;
  }

  const isGrailLimit =
    limit.value === GRAIL_MIN_DATE || limit.value === GRAIL_MAX_DATE;

  if (!isGrailLimit) {
    return limit;
  }

  return isValueOutsideGrailRange(value) ? limit : null;
}

function isTimeBoundaryErrorKey(
  errorKey?: TimeErrorKey,
): errorKey is TimeBoundaryErrorKey {
  return (
    errorKey === 'minBoundary' ||
    errorKey === 'minBoundaryNow' ||
    errorKey === 'maxBoundary' ||
    errorKey === 'maxBoundaryNowError' ||
    errorKey === 'minMaxBoundary'
  );
}

function parseLimit(
  limit?: TimeValue | string | null,
  relativeNow: number = TZDateMini.tz(getTimezone()).getTime(),
): TimeValue | null | undefined {
  if (typeof limit !== 'string') {
    // absoluteDate: '' is the sentinel for an unparseable expression — treat as absent.
    if (limit && !limit.absoluteDate) {
      return null;
    }
    return limit;
  }

  return parseTimeAsTimeValue(limit, relativeNow) ?? null;
}

function getBoundaryErrorKey(
  min?: TimeValue | null,
  max?: TimeValue | null,
): TimeBoundaryErrorKey | undefined {
  if (min && max) {
    return 'minMaxBoundary';
  }

  if (min) {
    return isNowTimeValue(min) ? 'minBoundaryNow' : 'minBoundary';
  }

  if (max) {
    return 'maxBoundary';
  }

  return undefined;
}

/**
 * Gets the timeframe boundary message data while hiding default Grail limits
 * unless the current value actually exceeds the Grail range.
 * @internal
 */
export function getTimeBoundaryMessageData(
  value: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null,
  min?: TimeValue | string | null,
  max?: TimeValue | string | null,
  precision: Precision = 'minutes',
  onlyWhenOutOfBounds: boolean = true,
  term?: string,
): TimeBoundaryMessageData | undefined {
  const relativeNow = TZDateMini.tz(getTimezone()).getTime();
  const parsedMin = parseLimit(min, relativeNow);
  const parsedMax = parseLimit(max, relativeNow);
  const filteredMin = filterGrailTimeValue(value, parsedMin);
  const filteredMax = filterGrailTimeValue(value, parsedMax);

  const errorKey = onlyWhenOutOfBounds
    ? getOutOfBoundsErrorKey(value, filteredMin, filteredMax)
    : getBoundaryErrorKey(filteredMin, filteredMax);

  if (!isTimeBoundaryErrorKey(errorKey)) {
    return undefined;
  }

  const { min: formattedMin, max: formattedMax } = getFormattedMinMax(
    filteredMin,
    filteredMax,
    precision,
  );

  const resolvedTerm =
    term ??
    (value && ('from' in value || 'to' in value) ? 'timeframe' : 'date');

  return {
    errorKey,
    values: {
      min: formattedMin,
      max: formattedMax,
      term: resolvedTerm,
    },
  };
}

/** validates a Timeframe or a TimeValue against their min and max borders */
function getOutOfBoundsErrorKey(
  updatedValue:
    | { from: TimeValue | null; to: TimeValue | null }
    | TimeValue
    | null,
  min?: TimeValue | null,
  max?: TimeValue | null,
): TimeErrorKey | undefined {
  let errorKey: TimeErrorKey | undefined;

  // Range value
  if (
    updatedValue &&
    ('from' in updatedValue || 'to' in updatedValue) &&
    isTimeframe(updatedValue, 'range')
  ) {
    const fromErrorKey = isOutOfBounds(updatedValue?.from?.value, min, max);
    const toErrorKey = isOutOfBounds(updatedValue?.to?.value, min, max);
    errorKey = fromErrorKey || toErrorKey;
  } else if (updatedValue && isTimeValue(updatedValue)) {
    // Single value
    errorKey = isOutOfBounds(updatedValue.value, min, max);
  }

  if (errorKey === 'maxBoundary' && isNowTimeValue(max)) {
    return 'maxBoundaryNowError';
  }
  if (errorKey === 'minBoundary' && isNowTimeValue(min)) {
    return 'minBoundaryNow';
  }
  return errorKey;
}

function isInvalidOrder(value: Partial<Timeframe>): boolean {
  if (isFullTimeframe(value)) {
    const timezone = getTimezone();
    const fromTime = new TZDateMini(
      value.from?.absoluteDate,
      timezone,
    ).getTime();
    const toTime = new TZDateMini(value.to?.absoluteDate, timezone).getTime();
    if (value.from && value.to && fromTime >= toTime) {
      return true;
    }
  }
  return false;
}

function getInvalidErrorKey(
  value: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null,
  valueMode?: 'single' | 'timeframeSelector' | 'dateTimePicker',
): TimeErrorKey | undefined {
  if (
    valueMode !== 'single' &&
    value &&
    !isTimeValue(value) &&
    (('from' in value && value.from !== undefined) ||
      ('to' in value && value.to !== undefined))
  ) {
    const isFromInvalid =
      value.from?.absoluteDate === '' && value.from?.value !== '';
    const isToInvalid = value.to?.absoluteDate === '' && value.to?.value !== '';

    if (isFromInvalid && isToInvalid) {
      return 'invalidFromTo';
    }
    if (isFromInvalid) {
      return 'invalidFrom';
    }
    if (isToInvalid) {
      return 'invalidTo';
    }
  }

  if (
    valueMode === 'single' &&
    value &&
    'absoluteDate' in value &&
    value.absoluteDate === '' &&
    value.value !== ''
  ) {
    return 'invalid';
  }

  return undefined;
}

/**
 * Determines the appropriate error key for a missing value.
 */
function getValueMissingErrorKey(
  value: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null,
  valueMode?: 'single' | 'timeframeSelector' | 'dateTimePicker',
): TimeErrorKey | undefined {
  if (value === null) {
    if (valueMode === 'single') {
      return 'singleValueMissing';
    }

    if (valueMode === 'dateTimePicker') {
      return 'missingFromTo';
    }

    return 'valueMissing';
  }

  if (!isTimeValue(value) && value.from === null && value.to === null) {
    if (valueMode === 'dateTimePicker') {
      return 'missingFromTo';
    }

    return 'valueMissing';
  }

  if (valueMode !== 'single' && isPartialTimeframe(value)) {
    if (!value.from) {
      return 'missingFrom';
    }
    if (!value.to) {
      return 'missingTo';
    }
  }

  return undefined;
}

/**
 * Maps {@link TimeErrorKey} values to the `validityStateKey` used when
 * dispatching the error through `formMessageDispatch` (the internal
 * `formfield-messages-state`). Must match {@link EXTERNAL_VALIDITY_KEY_MAP}
 * for all Strato-specific keys so that both dispatch paths (direct via
 * `validate()` and indirect via the form-runtime `RuntimeFieldMessageRegistration`)
 * produce the same `validityStateKey` — this lets the reducer's per-key
 * deduplication prevent duplicate messages when a control is inside a `<Form>`.
 *
 * Native browser keys (rangeUnderflow, rangeOverflow, valueMissing) are kept
 * as-is; Strato-specific keys use their own name (not the 'customError'
 * fallback). The reducer's `updateValidity` uses a matching set to decide
 * when to call `setCustomValidity` for Strato-specific keys.
 */
const VALIDITY_KEY_MAP: Partial<Record<TimeErrorKey, string>> = {
  minBoundary: 'rangeUnderflow',
  maxBoundary: 'rangeOverflow',
  singleValueMissing: 'valueMissing',
  missingFromTo: 'valueMissing',
  invalid: 'invalid',
  invalidFrom: 'invalidFrom',
  invalidTo: 'invalidTo',
  invalidFromTo: 'invalidFromTo',
  invalidOrder: 'invalidOrder',
  missingFrom: 'missingFrom',
  missingTo: 'missingTo',
  minMaxBoundary: 'minMaxBoundary',
};

/**
 * Maps internal {@link TimeErrorKey} values to the external {@link ValidityStateKey}
 * exposed in `ValidationState`. Keys that have native browser counterparts use
 * the native name (e.g. `singleValueMissing` → `'valueMissing'`); Strato-specific
 * keys use their own name so that they match {@link VALIDITY_KEY_MAP} exactly,
 * enabling per-key deduplication in `formfield-messages-state-reducer`.
 */
const EXTERNAL_VALIDITY_KEY_MAP: Partial<Record<TimeErrorKey, string>> = {
  minBoundary: 'rangeUnderflow',
  minBoundaryNow: 'rangeUnderflow',
  maxBoundary: 'rangeOverflow',
  maxBoundaryNowError: 'rangeOverflow',
  singleValueMissing: 'valueMissing',
  missingFromTo: 'valueMissing',
};

/**
 * Returns the {@link ValidityStateKey} for a {@link TimeErrorKey} for use in
 * `ValidationState` emitted to consumers. Applies native-key aliases so that
 * e.g. `singleValueMissing` surfaces as the native `valueMissing` key.
 * @internal
 */
export function getValidityStateKeyForTimeError(
  errorKey: TimeErrorKey,
): ValidityStateKey {
  return new ValidityStateKey(EXTERNAL_VALIDITY_KEY_MAP[errorKey] ?? errorKey);
}

export function validate(
  value: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null,
  min?: TimeValue | null,
  max?: TimeValue | null,
  options?: ValidateOptions,
): TimeErrorKey | undefined {
  const {
    formMessageDispatch,
    formerError: previousError,
    precision: validationPrecision = 'minutes',
    showError: showValidationError,
    checkOrder: shouldCheckOrder,
    ref: validationRef,
    expectedFormat: expectedValidationFormat = 'YYYY-MM-DD HH:mm:ss',
    valueMode: validationValueMode,
    required: isRequired,
    errorMessages,
  } = options ?? {};

  const term = validationValueMode === 'single' ? 'date' : 'timeframe';

  const timeBoundaryMessageData = getTimeBoundaryMessageData(
    value,
    min,
    max,
    validationPrecision,
    undefined,
    term,
  );

  const invalidOrderError =
    shouldCheckOrder && isInvalidOrder(value as Partial<Timeframe>)
      ? 'invalidOrder'
      : undefined;
  const invalidError = getInvalidErrorKey(value, validationValueMode);
  const valueMissingError = isRequired
    ? getValueMissingErrorKey(value, validationValueMode)
    : undefined;
  const outOfBoundsError = timeBoundaryMessageData?.errorKey;
  const errorKey =
    invalidOrderError ?? valueMissingError ?? outOfBoundsError ?? invalidError;

  if (errorKey) {
    const messageDef =
      errorMessages?.[errorKey] ?? DEFAULT_ERROR_MESSAGES[errorKey];

    const startTime = getFormattedTimeValue(
      isTimeValue(value) ? null : value?.from ?? null,
      validationPrecision,
    );

    // Only add the error message if the erroring input is already mounted
    validationRef?.current &&
      formMessageDispatch?.({
        type: FormFieldMessagesAction.ADDMESSAGE,
        payload: {
          id: errorKey,
          variant: 'error',
          messageType: 'formcontrol',
          message: (
            <FormattedMessage
              {...messageDef}
              values={{
                ...timeBoundaryMessageData?.values,
                expectedFormat: expectedValidationFormat,
                startTime,
              }}
            />
          ),
          validityStateKey: VALIDITY_KEY_MAP[errorKey] ?? 'customError',
          showError: showValidationError,
        },
      });
  }

  // cleanup old error message
  if (previousError && previousError !== errorKey) {
    validationRef?.current &&
      formMessageDispatch?.({
        type: FormFieldMessagesAction.REMOVEMESSAGE,
        payload: {
          id: previousError,
          messageType: 'formcontrol',
        },
      });
  }

  return errorKey;
}

/**
 * Formats the human-readable message for a time validation error key.
 *
 * Handles interpolation for boundary errors (`{min}`, `{max}`) and format
 * errors (`{expectedFormat}`). Falls back to the error key string when no
 * message descriptor is found.
 *
 * @internal
 */
export function formatTimeValidationMessage(
  errorKey: TimeErrorKey,
  intl: IntlShape,
  options?: {
    value?: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null;
    min?: TimeValue | null;
    max?: TimeValue | null;
    precision?: Precision;
    expectedFormat?: string;
    errorMessages?: Partial<Record<TimeErrorKey, MessageDescriptor>>;
  },
): string {
  const {
    value,
    min,
    max,
    precision = 'minutes',
    expectedFormat,
    errorMessages,
  } = options ?? {};

  const msgDef = errorMessages?.[errorKey] ?? DEFAULT_ERROR_MESSAGES[errorKey];

  if (!msgDef) {
    return errorKey;
  }

  if (isTimeBoundaryErrorKey(errorKey)) {
    // Pass onlyWhenOutOfBounds=false so we always get formatted min/max values even
    // when the current value is null or no longer out of bounds (stale error key).
    return intl.formatMessage(
      msgDef,
      getTimeBoundaryMessageData(value ?? null, min, max, precision, false)
        ?.values,
    );
  }

  if (errorKey === 'invalidOrder') {
    return intl.formatMessage(msgDef, {
      startTime: getFormattedTimeValue(
        isTimeValue(value) ? null : value?.from ?? null,
        precision,
      ),
    });
  }

  return intl.formatMessage(
    msgDef,
    errorKey === 'invalid' ||
      errorKey === 'invalidFrom' ||
      errorKey === 'invalidTo' ||
      errorKey === 'invalidFromTo'
      ? { expectedFormat: expectedFormat ?? '' }
      : undefined,
  );
}
